import React, { useState } from "react";

// @material-ui/core components
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import InputAdornment from "@material-ui/core/InputAdornment";
import Icon from "@material-ui/core/Icon";
import Typography from "@material-ui/core/Typography";
import Link from "@material-ui/core/Link";
import Box from "@material-ui/core/Box";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import { FormControlLabel } from "@material-ui/core";
import { Checkbox } from "@material-ui/core";

// routing
import { useHistory } from "react-router-dom";

// @material-ui/icons
import Face from "@material-ui/icons/Face";
import Group from "@material-ui/icons/Group";
import Email from "@material-ui/icons/Email";
import PolicyIcon from "@material-ui/icons/Policy";
import SearchIcon from "@material-ui/icons/Search";
import AccountCircle from "@material-ui/icons/AccountCircle";
import Business from "@material-ui/icons/Business";
import { Check } from "@material-ui/icons";

// core components
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import Input from "components/CustomInput/Input.js";
import InfoArea from "components/InfoArea/InfoArea.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";

import styles from "./Styles/registerPageStyle";
import { useTranslation, Trans } from "react-i18next";
import { resolveAuthError } from "../../utils/authErrorI18n";

const API_URL = process.env.REACT_APP_API;
const useStyles = makeStyles(styles);

export default function RegisterPage() {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));

  const [fullName, setFullName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [institution, setInstitution] = useState("");
  const [password, setPassword] = useState("");
  const [passVerify, setPassVerify] = useState("");
  const [message, setMessage] = useState(null);
  const [reqError, setReqError] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { t } = useTranslation(["registerPage", "privacyPolicy"]);

  const privacySections = [
    { titleKey: "A1", descriptionKey: "descriptionA1", useTrans: false },
    { titleKey: "A2", descriptionKey: "descriptionA2", useTrans: false },
    { titleKey: "A3", descriptionKey: "descriptionA3", useTrans: false },
    { titleKey: "A4", descriptionKey: "descriptionA4", useTrans: false },
    { titleKey: "A5", descriptionKey: "descriptionA5", useTrans: false },
    { titleKey: "A6", descriptionKey: "descriptionA6", useTrans: false },
    { titleKey: "A7", descriptionKey: "descriptionA7", useTrans: false },
    { titleKey: "A8", descriptionKey: "descriptionA8", useTrans: true },
    { titleKey: "A9", descriptionKey: "descriptionA9", useTrans: false },
  ];

  /**
   * Traduce un error de registro a partir del errorCode devuelto por backend.
   * Si no hay código conocido, usa mensaje fallback traducible.
   */
  const translateRegisterError = (errorCode, fallbackMessage) =>
    resolveAuthError(t, {
      page: "register",
      errorCode,
      fallbackMessage,
    });

  const signIn = async () => {
    if (isSubmitting) return;

    setReqError(null);
    setMessage(null);

    const isEmpty = (v) => !v || v.toString().trim() === "";

    if ([email, password, passVerify].some(isEmpty)) {
      setReqError(t("register-error-email-and-password-required"));
      return;
    }

    if (password !== passVerify) {
      setReqError(t("register-error-passwords-not-equal"));
      return;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email.trim())) {
      setReqError(t("register-error-email-pattern-invalid"));
      return;
    }

    if (!termsAccepted) {
      setReqError(t("register-terms-required"));
      return;
    }

    setIsSubmitting(true);

    try {
      const requestBody = {
        email: email.trim(),
        password: password.trim(),
        rol: "basico",
      };

      if (fullName.trim()) {
        requestBody.nombre = fullName.trim();
      }

      if (username.trim()) {
        requestBody.username = username.trim();
      }

      if (institution.trim()) {
        requestBody.institution = institution.trim();
      }

      const regResp = await fetch(`/api/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      let response = null;

      try {
        response = await regResp.json();
      } catch (parseError) {
        response = null;
      }

      if (!regResp.ok || response?.ok !== true) {
        setReqError(
          translateRegisterError(
            response?.errorCode,
            response?.mensaje || t("register-error-generic")
          )
        );
        return;
      }

      setMessage(t("register-success-created"));
    } catch (err) {
      setReqError(
        translateRegisterError(
          err?.errorCode,
          err?.message || t("register-error-generic")
        )
      );
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    signIn();
  };

  return (
    <div className={classes.container}>
      <GridContainer justify="center">
        <GridItem xs={12} sm={12} md={10}>
          <Card className={classes.cardSignup}>
            <h2 className={classes.cardTitle}>{t("register-title")}</h2>

            <CardBody>
              <GridContainer justify="center" spacing={3}>
                <GridItem xs={12} sm={12} md={5}>
                  <InfoArea
                    title={t("register-info-who-title")}
                    description={t("register-info-who-description")}
                    icon={Group}
                    iconColor="primary"
                  />

                  <InfoArea
                    title={t("register-info-privacy-title")}
                    description={t("register-info-privacy-description")}
                    icon={PolicyIcon}
                    iconColor="primary"
                  />

                  <InfoArea
                    title={t("register-info-tool-title")}
                    description={
                      <>
                        <p style={{ marginTop: 0, marginBottom: 8 }}>
                          {t("register-info-tool-description")}
                        </p>
                        <Link
                          href="https://github.com/uned-cibergid-projects/safemountain"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t("register-info-tool-repo-link")}
                        </Link>
                      </>
                    }
                    icon={SearchIcon}
                    iconColor="primary"
                  />
                </GridItem>

                <GridItem xs={12} sm={10} md={5}>
                  <form className={classes.form} onSubmit={handleSubmit}>
                    <Input
                      formControlProps={{
                        fullWidth: true,
                        className: classes.customFormControlClasses,
                      }}
                      inputProps={{
                        startAdornment: (
                          <InputAdornment
                            position="start"
                            className={classes.inputAdornment}
                          >
                            <Face className={classes.inputAdornmentIcon} />
                          </InputAdornment>
                        ),
                        placeholder: t("register-input-fullname-placeholder"),
                        value: fullName,
                        onChange: (e) => setFullName(e.target.value),
                      }}
                    />

                    <Input
                      formControlProps={{
                        fullWidth: true,
                        className: classes.customFormControlClasses,
                      }}
                      inputProps={{
                        startAdornment: (
                          <InputAdornment
                            position="start"
                            className={classes.inputAdornment}
                          >
                            <AccountCircle
                              className={classes.inputAdornmentIcon}
                            />
                          </InputAdornment>
                        ),
                        placeholder: t("register-input-username-placeholder"),
                        value: username,
                        onChange: (e) => setUsername(e.target.value),
                      }}
                    />

                    <Input
                      formControlProps={{
                        fullWidth: true,
                        className: classes.customFormControlClasses,
                      }}
                      inputProps={{
                        startAdornment: (
                          <InputAdornment
                            position="start"
                            className={classes.inputAdornment}
                          >
                            <Business className={classes.inputAdornmentIcon} />
                          </InputAdornment>
                        ),
                        placeholder: t("register-input-institution-placeholder"),
                        value: institution,
                        onChange: (e) => setInstitution(e.target.value),
                      }}
                    />

                    <Input
                      formControlProps={{
                        fullWidth: true,
                        className: classes.customFormControlClasses,
                      }}
                      inputProps={{
                        startAdornment: (
                          <InputAdornment
                            position="start"
                            className={classes.inputAdornment}
                          >
                            <Email className={classes.inputAdornmentIcon} />
                          </InputAdornment>
                        ),
                        placeholder: t("register-input-email-placeholder"),
                        value: email,
                        onChange: (e) => setEmail(e.target.value),
                      }}
                    />

                    <Input
                      passwordToggle
                      formControlProps={{
                        fullWidth: true,
                        className: classes.customFormControlClasses,
                      }}
                      inputProps={{
                        startAdornment: (
                          <InputAdornment
                            position="start"
                            className={classes.inputAdornment}
                          >
                            <Icon className={classes.inputAdornmentIcon}>
                              lock_outline
                            </Icon>
                          </InputAdornment>
                        ),
                        placeholder: t("register-input-password-placeholder"),
                        value: password,
                        onChange: (e) => setPassword(e.target.value),
                      }}
                    />

                    <Input
                      passwordToggle
                      formControlProps={{
                        fullWidth: true,
                        className: classes.customFormControlClasses,
                      }}
                      inputProps={{
                        startAdornment: (
                          <InputAdornment
                            position="start"
                            className={classes.inputAdornment}
                          >
                            <Icon className={classes.inputAdornmentIcon}>
                              lock_outline
                            </Icon>
                          </InputAdornment>
                        ),
                        placeholder: t(
                          "register-input-password-confirm-placeholder"
                        ),
                        value: passVerify,
                        onChange: (e) => setPassVerify(e.target.value),
                      }}
                    />

                    <FormControlLabel
                      classes={{
                        root: classes.checkboxLabelControl,
                        label: classes.checkboxLabel,
                      }}
                      control={
                        <Checkbox
                          checked={termsAccepted}
                          onChange={(e) => setTermsAccepted(e.target.checked)}
                          checkedIcon={<Check className={classes.checkedIcon} />}
                          icon={<Check className={classes.uncheckedIcon} />}
                          classes={{
                            checked: classes.checked,
                            root: classes.checkRoot,
                          }}
                        />
                      }
                      label={
                        <span>
                          {t("register-terms-accept-prefix")}{" "}
                          <Link
                            component="button"
                            variant="body2"
                            onClick={(e) => {
                              e.preventDefault();
                              setTermsOpen(true);
                            }}
                          >
                            {t("register-terms-link")}
                          </Link>
                          .
                        </span>
                      }
                    />

                    <Box mt={1} className={classes.center}>
                      <Typography
                        variant="body2"
                        style={{
                          color: "green",
                          minHeight: message ? 24 : 0,
                          wordBreak: "break-word",
                        }}
                      >
                        {message || ""}
                      </Typography>

                      <Typography
                        variant="body2"
                        style={{
                          color: "red",
                          minHeight: reqError ? 24 : 0,
                          wordBreak: "break-word",
                        }}
                      >
                        {reqError || ""}
                      </Typography>
                    </Box>

                    <Box mt={2} className={classes.center}>
                      <Button
                        round
                        color="primary"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {t("register-action-submit")}
                      </Button>
                    </Box>

                    <Box mt={4} className={classes.center}>
                      <Typography className={classes.createAccText}>
                        {t("register-have-account")}
                      </Typography>

                      <Box mt={1}>
                        <Button
                          round
                          color="info"
                          type="button"
                          onClick={() => history.push("/login")}
                        >
                          {t("register-action-login")}
                        </Button>
                      </Box>
                    </Box>
                  </form>

                  <Dialog
                    open={termsOpen}
                    onClose={() => setTermsOpen(false)}
                    maxWidth="sm"
                    fullWidth
                    fullScreen={fullScreenDialog}
                  >
                    <DialogTitle>{t("privacyPolicy:title")}</DialogTitle>

                    <DialogContent dividers>
                      {privacySections.map((section) => (
                        <React.Fragment key={section.titleKey}>
                          <Typography
                            align="center"
                            variant="h5"
                            className={classes.title}
                          >
                            {t(`privacyPolicy:${section.titleKey}`)}
                          </Typography>

                          <Typography
                            align="justify"
                            className={classes.paragraph}
                            style={{ whiteSpace: "pre-line" }}
                          >
                            {section.useTrans ? (
                              <Trans
                                i18nKey={`privacyPolicy:${section.descriptionKey}`}
                                ns="privacyPolicy"
                                components={{
                                  dpjLink: (
                                    <a
                                      href="https://www.uned.es/dpj"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    />
                                  ),
                                  eofficeLink: (
                                    <a
                                      href="https://uned.sede.gob.es/"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    />
                                  ),
                                  privacyLink: (
                                    <a
                                      href="https://descargas.uned.es/publico/pdf/Politica_privacidad_UNED.pdf"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    />
                                  ),
                                  aepd: (
                                    <a
                                      href="https://www.aepd.es"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    />
                                  ),
                                }}
                              />
                            ) : (
                              t(`privacyPolicy:${section.descriptionKey}`)
                            )}
                          </Typography>
                        </React.Fragment>
                      ))}
                    </DialogContent>

                    <DialogActions style={{ justifyContent: "center" }}>
                      <Button
                        round
                        color="primary"
                        type="button"
                        onClick={() => setTermsOpen(false)}
                      >
                        Ok
                      </Button>
                    </DialogActions>
                  </Dialog>
                </GridItem>
              </GridContainer>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    </div>
  );
}